/**
 * LO ÚLTIMO QUE SE SUPO, PARA PODER MIRARLO SIN SEÑAL.
 *
 * El técnico abre la aplicación en la obra para ver tres cosas: qué tiene
 * hoy, qué tiene esta semana y qué hizo. Las tres salían de una consulta al
 * servidor, así que sin cobertura la pantalla quedaba en un error —se
 * comprobó: «Mi programación» y «Mi historial» no abrían—.
 *
 * Esto guarda la última respuesta buena de cada una y la devuelve cuando no
 * hay red. No es dato fresco y las pantallas lo dicen; pero saber lo de esta
 * mañana es infinitamente más útil que una pantalla en rojo.
 *
 * NUNCA se guarda aquí nada que sirva para decidir plata o permisos: solo lo
 * que el técnico necesita leer para trabajar.
 */

const PREFIJO = 'mega.ultimo.'

export function guardarUltimo<T>(clave: string, datos: T): void {
  try {
    localStorage.setItem(
      PREFIJO + clave,
      JSON.stringify({ cuando: new Date().toISOString(), datos }),
    )
  } catch {
    /* Sin espacio o en modo privado: se pierde el modo sin señal, nada más. */
  }
}

export function leerUltimo<T>(clave: string): T | null {
  try {
    const crudo = localStorage.getItem(PREFIJO + clave)
    if (!crudo) return null
    const { datos } = JSON.parse(crudo) as { cuando: string; datos: T }
    return datos ?? null
  } catch {
    return null
  }
}

/** De cuándo es lo guardado. Para poder avisar de que no está fresco. */
export function cuandoSeGuardo(clave: string): string | null {
  try {
    const crudo = localStorage.getItem(PREFIJO + clave)
    if (!crudo) return null
    return (JSON.parse(crudo) as { cuando: string }).cuando ?? null
  } catch {
    return null
  }
}

/**
 * Envuelve una consulta para que sobreviva sin señal.
 *
 * Sin red devuelve lo guardado sin intentar nada. Con red intenta y, si el
 * servidor no contesta —la raya que no transmite, tan común en obra—, cae a
 * lo guardado antes de dar error.
 */
export async function conRespaldo<T>(clave: string, pedir: () => Promise<T>): Promise<T> {
  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    const guardado = leerUltimo<T>(clave)
    if (guardado !== null) return guardado
  }

  try {
    const fresco = await pedir()
    guardarUltimo(clave, fresco)
    return fresco
  } catch (fallo) {
    const guardado = leerUltimo<T>(clave)
    if (guardado !== null) return guardado
    throw fallo
  }
}
